import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { LinkButton, StandardButton } from '@eika/button';
import { CurrencyInput, formatCurrencyValue } from '@eika/currency-input';
import { Loader } from '@eika/loader';
import { ButtonToggles } from '@eika/radio-button';

import { AppContext, DEAFULT_LAANEBELOP, DEFAULT_MARKEDSRENTE } from '../containers/kalkulatorState';
import { useConfig } from '../default-config';
import { LaanerammeWeb } from '../generated-types';
import { piwikTrackId, piwikTrackName } from '../gtm/gtmUtils';
import { useLaaneramme, useMarkedsrente } from '../hooks/hooks';

import Kuleramme from '../resources/kuleramme.svg';

const Laaneramme: React.FC = () => {
    const { t } = useTranslation();
    const { links } = useConfig();
    const navigate = useNavigate();
    const { data: markedsrente } = useMarkedsrente();
    const { laaneparam, setLaaneparam } = useContext(AppContext);

    const laanerammeWeb: LaanerammeWeb = {
        aarsinntekt: laaneparam.aarsinntekt!,
        gjeld: laaneparam.gjeld!,
        antallBarn: Number(laaneparam.antallBarn),
        antallBiler: Number(laaneparam.antallBiler),
        antallLaantakere: Number(laaneparam.antallLaantakere),
        markedsrente: laaneparam.rente,
        nedbetalingstid: laaneparam.nedbetalingstid,
    };

    const { data: laaneRamme, isLoading } = useLaaneramme(laanerammeWeb);

    const laaneRammeSvar = (): string => {
        if (laaneRamme === undefined) {
            return t('laaneramme.laanerammeSpoersmaal');
        }
        if (laaneRamme.beloep === 0) {
            return t('laaneramme.laanerammeSvarIkkeLaan');
        }
        return t('laaneramme.laanerammeSvar');
    };

    const handleLaaneparamChange = (value: string, key: string) => {
        setLaaneparam({
            ...laaneparam,
            [key]: value,
        });
    };

    const onClickSoekBoliglaan = () => {
        window.location.replace(links.boliglaanurl.url);
    };

    const onClickLinkButtonHvaKosterLaanet = () => {
        setLaaneparam({
            ...laaneparam,
            rente: markedsrente?.markedsrente || DEFAULT_MARKEDSRENTE,
            laanebeloep: laaneRamme?.beloep || DEAFULT_LAANEBELOP,
        });

        navigate('/kostnad');
    };

    return (
        <div className="laaneramme">
            <div className="sgw-layout">
                <div className="sgw-layout-item one">
                    <div>
                        <h2 className="margin-bottom-medium-large">{t('laaneramme.boliglaanKalkulator')}</h2>
                        <div className="margin-bottom-larger">
                            <span className="font-size-small bold padding-right-medium-large">
                                {t('laaneramme.hvorMyeKanJegLaane')}
                            </span>
                            <LinkButton
                                className="font-size-small"
                                onClick={onClickLinkButtonHvaKosterLaanet}
                                data-track-id={piwikTrackId('linker')}
                                data-track-linkname={piwikTrackName('hva-koster-laanet')}
                            >
                                {t('laaneramme.hvaKosterLaanet')}
                            </LinkButton>
                        </div>
                        <div className="row row-gap-large">
                            <ButtonToggles
                                label={t('laaneramme.antallLaantakere')}
                                dataTrackId={piwikTrackId('laaneramme-antall-laantakere')}
                                onChange={(value) => {
                                    handleLaaneparamChange(value, 'antallLaantakere');
                                }}
                                selectedOption={laaneparam.antallLaantakere}
                                options={[
                                    { label: '1', value: '1' },
                                    { label: '2', value: '2' },
                                ]}
                            />
                            <ButtonToggles
                                label={t('laaneramme.antallBarn')}
                                dataTrackId={piwikTrackId('laaneramme-antall-barn')}
                                className="max-width-desktop-80-precent"
                                onChange={(value) => {
                                    handleLaaneparamChange(value, 'antallBarn');
                                }}
                                selectedOption={laaneparam.antallBarn.toString()}
                                options={[
                                    { label: t('laaneramme.ingen'), value: '0' },
                                    { label: '1', value: '1' },
                                    { label: '2', value: '2' },
                                    { label: '3', value: '3' },
                                    { label: '4', value: '4' },
                                    { label: '5+', value: '5' },
                                ]}
                            />
                            <ButtonToggles
                                label={t('laaneramme.antallBiler')}
                                dataTrackId={piwikTrackId('antall-biler')}
                                onChange={(value) => {
                                    handleLaaneparamChange(value, 'antallBiler');
                                }}
                                selectedOption={laaneparam.antallBiler}
                                options={[
                                    { label: t('laaneramme.ingen'), value: '0' },
                                    { label: '1', value: '1' },
                                    { label: '2+', value: '2' },
                                ]}
                            />
                            <CurrencyInput
                                name="inntekt"
                                placeholder="0 kr"
                                className="max-width-200"
                                label={t('laaneramme.samletAarsinntektFoerSkatt')}
                                value={laaneparam.aarsinntekt}
                                onChange={(e: { type: string; target: { name: string; value: string | number } }) => {
                                    handleLaaneparamChange(String(e.target.value), 'aarsinntekt');
                                }}
                            />
                            <CurrencyInput
                                name="gjeld"
                                placeholder="0 kr"
                                className="max-width-200"
                                label={t('laaneramme.samletGjeld')}
                                sublabel={t('laaneramme.samletGjeldSublabel')}
                                value={laaneparam.gjeld}
                                onChange={(e: { type: string; target: { name: string; value: string | number } }) => {
                                    handleLaaneparamChange(String(e.target.value), 'gjeld');
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className="sgw-layout-item two">
                    <div className="row align-items">
                        <img src={Kuleramme} alt="kuleramme" />
                        <div className="margin-bottom-large">
                            {isLoading ? (
                                <Loader mini />
                            ) : (
                                <div className="max-300">
                                    <span className="typography-ingress">{laaneRammeSvar()}</span>
                                    {laaneRamme && laaneRamme?.beloep !== 0 && (
                                        <div>
                                            <span className="number-extra-large">
                                                {formatCurrencyValue(Number(laaneRamme?.beloep))}
                                            </span>{' '}
                                            kr
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="margin-bottom-large">
                            <StandardButton
                                onClick={onClickSoekBoliglaan}
                                data-track-id={piwikTrackId('linker')}
                                data-track-linkname={piwikTrackName('soek-boliglaan')}
                            >
                                {t('laaneramme.soekBoliglaan')}
                            </StandardButton>
                        </div>
                        <LinkButton
                            onClick={onClickLinkButtonHvaKosterLaanet}
                            data-track-id={piwikTrackId('linker')}
                            data-track-linkname={piwikTrackName('hva-koster-laanet')}
                        >
                            {t('laaneramme.hvaKosterLaanet')}
                        </LinkButton>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Laaneramme;
